import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { Viewer } from '../Viewer';
import { generateDefaultUserIcon } from '@framework/utils';
import { useFollowingUser } from '../useFollowingUser';
type Props = {
  viewer: Viewer;
  setClientViewBox: (viewBox: Rect) => void;
};

export const ViewerIcon: React.FC<Props> = ({ viewer, setClientViewBox }: Props) => {
  const { t } = useTranslation();
  const { startFollowing, isFollowing } = useFollowingUser();
  const isFollowingThisViewer = isFollowing(viewer.userId);

  const handleClick = useCallback(() => {
    // フォロー開始と同時にビューポートを即座にジャンプ
    startFollowing(viewer.userId);
    setClientViewBox(viewer.rect);
  }, [startFollowing, viewer.userId, setClientViewBox, viewer.rect]);

  return (
    <DarkTooltip
      tooltip={
        <div className="text-center">
          <div>{viewer.userName}</div>
          <div className="text-xs text-gray-300">{t('canvas.viewer.icon.tooltip')}</div>
        </div>
      }
      placement={'bottom'}
    >
      {/* 背後にグレーの丸を配置して、その手前に中心を揃えてアイコン画像を表示している */}
      {/* 詳細:https://github.com/levii/balus-app/pull/3019 */}
      <div
        className={`relative size-6 cursor-pointer rounded-full bg-gray-400 focus:border-white focus:outline-none ${isFollowingThisViewer ? 'ring-2 ring-orange-500' : ''}`}
        onClick={handleClick}
      >
        {viewer.imageUrl ? (
          <img
            className="absolute left-1/2 top-1/2 size-5/6 -translate-x-1/2 -translate-y-1/2 transform-gpu overflow-hidden rounded-full object-cover"
            src={viewer.imageUrl}
            alt={viewer.userName}
          />
        ) : (
          <div className="absolute left-1/2 top-1/2 size-5/6 -translate-x-1/2 -translate-y-1/2 transform-gpu overflow-hidden rounded-full object-cover">
            {generateDefaultUserIcon({
              id: viewer.userId,
              name: viewer.userName,
              isAnonymous: viewer.isAnonymous,
              size: 20,
            })}
          </div>
        )}
      </div>
    </DarkTooltip>
  );
};
